/**
 * Хосты сайта и режим маршрутизации филиалов.
 *
 * Модуль-лист: ничего не импортирует, поэтому его безопасно тянуть и из
 * branches-config, и из proxy.js, и из клиентских компонентов.
 *
 * Переменные окружения (все необязательные; без них поведение = прод ur-met.ru):
 *   PUBLIC_HOST          хост филиала по умолчанию (spb). Дефолт: хост из
 *                        NEXT_PUBLIC_SITE_URL, иначе ur-met.ru.
 *   PUBLIC_HOSTS         allowlist публичных хостов через запятую (редиректы,
 *                        аналитика). Дефолт: ur-met.ru,www.ur-met.ru.
 *   BRANCH_ROUTING       path | host. Дефолт path (/geo/<code>/ на одном хосте).
 *   BRANCH_HOST_<CODE>   хост филиала в режиме host, напр. BRANCH_HOST_MSK=msk.kontinental.pro.
 *                        Филиал без своего хоста и в режиме host живёт по /geo/<code>/.
 *   COOKIE_DOMAIN        Domain для auth-cookie (общая сессия поддоменов), напр. .kontinental.pro.
 *
 * На сервере конфиг читается из process.env во время выполнения. В браузере
 * process.env недоступен (кроме NEXT_PUBLIC_*), поэтому корневой layout
 * передаёт тот же конфиг в SiteHostProvider, и тот кладёт его сюда через
 * setClientSiteHostConfig() — до рендера дочерних компонентов.
 */

export type BranchRouting = 'path' | 'host';

export interface SiteHostConfig {
    routing: BranchRouting;
    /** Хост филиала по умолчанию, без порта: ur-met.ru */
    publicHost: string;
    /** Origin филиала по умолчанию без завершающего слэша: https://ur-met.ru */
    publicOrigin: string;
    /** PUBLIC_HOSTS (+ явный PUBLIC_HOST), без хостов филиалов */
    publicHosts: string[];
    /** Хосты филиалов (только для режима host): { msk: 'msk.kontinental.pro' } */
    branchHosts: Record<string, string>;
}

export const DEFAULT_BRANCH_CODE = 'spb';
const DEFAULT_ORIGIN = 'https://ur-met.ru';
const DEFAULT_PUBLIC_HOSTS = ['ur-met.ru', 'www.ur-met.ru'];
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1', '0.0.0.0']);

type Env = Record<string, string | undefined>;

/** 'Msk.Kontinental.pro:443.' -> 'msk.kontinental.pro' */
export function normalizeHost(value?: string | null): string {
    if (!value) return '';
    let host = String(value).split(',')[0].trim().toLowerCase();
    host = host.replace(/^[a-z]+:\/\//, '').replace(/\/.*$/, '');
    if (host.startsWith('[')) {
        const end = host.indexOf(']');
        return end > 0 ? host.slice(0, end + 1) : host;
    }
    return host.replace(/:\d+$/, '').replace(/\.$/, '');
}

function hostFromUrl(value?: string): string {
    if (!value) return '';
    try { return normalizeHost(new URL(value).host); } catch { return ''; }
}

function splitHosts(value?: string): string[] {
    return (value || '').split(',').map(normalizeHost).filter(Boolean);
}

export function readSiteHostConfig(env: Env): SiteHostConfig {
    const explicitHost = normalizeHost(env.PUBLIC_HOST);
    const siteUrl = (env.NEXT_PUBLIC_SITE_URL || '').trim().replace(/\/+$/, '');
    const publicHost = explicitHost || hostFromUrl(siteUrl) || 'ur-met.ru';
    // Без PUBLIC_HOST origin остаётся ровно прежним: NEXT_PUBLIC_SITE_URL || https://ur-met.ru.
    const publicOrigin = explicitHost ? `https://${explicitHost}` : (siteUrl || DEFAULT_ORIGIN);
    const routing: BranchRouting = (env.BRANCH_ROUTING || '').trim().toLowerCase() === 'host' ? 'host' : 'path';
    const branchHosts: Record<string, string> = {};
    if (routing === 'host') {
        for (const [key, value] of Object.entries(env)) {
            const match = key.match(/^BRANCH_HOST_([A-Z0-9_]+)$/);
            const host = normalizeHost(value);
            if (!match || !host) continue;
            const code = match[1].toLowerCase();
            // Филиал по умолчанию всегда живёт на PUBLIC_HOST.
            if (code !== DEFAULT_BRANCH_CODE && host !== publicHost) branchHosts[code] = host;
        }
    }
    const listed = splitHosts(env.PUBLIC_HOSTS);
    return {
        routing,
        publicHost,
        publicOrigin,
        // Явный PUBLIC_HOST публичен всегда; хост из NEXT_PUBLIC_SITE_URL — нет:
        // локальный NEXT_PUBLIC_SITE_URL=http://localhost не должен включать боевую Метрику.
        publicHosts: Array.from(new Set([...(listed.length ? listed : DEFAULT_PUBLIC_HOSTS), ...(explicitHost ? [explicitHost] : [])])),
        branchHosts,
    };
}

let serverConfig: SiteHostConfig | null = null;
let clientConfig: SiteHostConfig | null = null;

/** Вызывается SiteHostProvider в браузере; на сервере игнорируется. */
export function setClientSiteHostConfig(config: SiteHostConfig | null | undefined): void {
    if (typeof window !== 'undefined' && config) clientConfig = config;
}

/** Только для тестов: сбросить кеш и (опционально) подставить конфиг. */
export function __setSiteHostConfigForTests(config: SiteHostConfig | null): void {
    serverConfig = config;
    clientConfig = config;
}

export function getSiteHostConfig(): SiteHostConfig {
    if (typeof window !== 'undefined') {
        // До монтирования провайдера — поведение пути (как сейчас на проде).
        return clientConfig || (clientConfig = readSiteHostConfig({
            NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
        }));
    }
    return serverConfig || (serverConfig = readSiteHostConfig(process.env as Env));
}

export function getPublicHost(): string {
    return getSiteHostConfig().publicHost;
}

export function getBranchRouting(): BranchRouting {
    return getSiteHostConfig().routing;
}

/** Хост филиала. Для spb и для филиала без собственного хоста — PUBLIC_HOST. */
export function getBranchHost(code: string): string {
    const config = getSiteHostConfig();
    return config.branchHosts[code] || config.publicHost;
}

/** Живёт ли филиал на собственном хосте (режим host + BRANCH_HOST_<CODE>). */
export function isHostRoutedBranch(code: string): boolean {
    const config = getSiteHostConfig();
    return config.routing === 'host' && Boolean(config.branchHosts[code]);
}

/** Филиал, которому принадлежит хост, или null (главный/чужой хост). */
export function branchCodeForHost(host?: string | null): string | null {
    const normalized = normalizeHost(host);
    if (!normalized) return null;
    const config = getSiteHostConfig();
    if (config.routing !== 'host') return null;
    for (const [code, branchHost] of Object.entries(config.branchHosts)) {
        if (branchHost === normalized) return code;
    }
    return null;
}

/** https://<host филиала> без завершающего слэша. */
export function siteUrlForBranch(code: string = DEFAULT_BRANCH_CODE): string {
    const config = getSiteHostConfig();
    const host = config.routing === 'host' ? config.branchHosts[code] : undefined;
    return host ? `https://${host}` : config.publicOrigin.replace(/\/+$/, '');
}

/** Все публичные хосты: PUBLIC_HOSTS + явный PUBLIC_HOST + хосты филиалов (режим host). */
export function getPublicHosts(): string[] {
    const config = getSiteHostConfig();
    const hosts = [...config.publicHosts, ...Object.values(config.branchHosts)];
    return Array.from(new Set(hosts.filter((host) => host && !LOCAL_HOSTS.has(host))));
}

export function isPublicHost(host?: string | null): boolean {
    const normalized = normalizeHost(host);
    return Boolean(normalized) && getPublicHosts().includes(normalized);
}

export function isLocalHost(host?: string | null): boolean {
    return LOCAL_HOSTS.has(normalizeHost(host));
}

/** Domain для auth-cookie или undefined (host-only cookie, как сейчас). */
export function getCookieDomain(): string | undefined {
    if (typeof window !== 'undefined') return undefined;
    const value = (process.env.COOKIE_DOMAIN || '').trim();
    return value || undefined;
}
